// Laporan Pelajar (Prompt F) — ringkasan yuran per pelajar: jumlah bil, dibayar, baki, status.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Printer, Loader2, Users } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { formatRM } from '../../../lib/format';
import { paidByInvoice, studentSummary } from '../../../lib/fees';
import { toCsv, downloadCsv, printLaporan } from '../../../lib/laporan';
import PageHeader from '../../../components/PageHeader';
import { CardSkeleton } from '../../../components/Skeletons';
import DataTable from '../../../components/DataTable';
import StatusPill from '../../../components/StatusPill';
import { Button } from '../../../components/ui/button';

export default function LaporanPelajar() {
  const { t } = useTranslation();
  const [kelasId, setKelasId] = useState('semua');
  const [classes, setClasses] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: students }, { data: cls }, { data: invoices }, { data: pays }] = await Promise.all([
      supabase.from('students').select('id, nama, class_id').eq('aktif', true),
      supabase.from('classes').select('id, nama'),
      supabase.from('invoices').select('id, student_id, jumlah_bayar_rm'),
      supabase.from('payments').select('invoice_id, jumlah_rm').eq('voided', false),
    ]);
    const paidMap = paidByInvoice(pays || []);
    const cMap = Object.fromEntries((cls || []).map((c) => [c.id, c.nama]));
    const invByS = {};
    (invoices || []).forEach((inv) => { (invByS[inv.student_id] = invByS[inv.student_id] || []).push(inv); });
    const out = (students || [])
      .filter((s) => kelasId === 'semua' || s.class_id === kelasId)
      .map((s) => {
        const invs = invByS[s.id] || [];
        const sum = studentSummary(invs, paidMap);
        return { id: s.id, nama: s.nama, kelas: cMap[s.class_id] || '-', bil: invs.length, jumlah: sum.billed, bayar: sum.paid, baki: sum.outstanding, status: sum.status };
      })
      .sort((a, b) => a.nama.localeCompare(b.nama));
    setClasses(cls || []);
    setRows(out);
    setLoading(false);
  }, [kelasId]);

  useEffect(() => { load(); }, [load]);
  const totals = useMemo(() => rows.reduce((a, r) => ({ jumlah: a.jumlah + r.jumlah, bayar: a.bayar + r.bayar, baki: a.baki + r.baki }), { jumlah: 0, bayar: 0, baki: 0 }), [rows]);

  const doCsv = () => downloadCsv('laporan-pelajar.csv', toCsv(rows, [
    { label: 'Pelajar', get: (r) => r.nama },
    { label: 'Kelas', get: (r) => r.kelas },
    { label: 'Bil', get: (r) => r.bil },
    { label: 'Jumlah Bil (RM)', get: (r) => r.jumlah.toFixed(2) },
    { label: 'Dibayar (RM)', get: (r) => r.bayar.toFixed(2) },
    { label: 'Baki (RM)', get: (r) => r.baki.toFixed(2) },
    { label: 'Status', get: (r) => r.status },
  ]));

  const inputCls = 'h-10 px-3 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand';

  return (
    <div>
      <div className="print:hidden">
        <PageHeader title={t('lapPelajar')} subtitle={t('lapPelajarSub')} breadcrumbs={[{ label: t('laporan') }, { label: t('lapPelajar') }]} />
      </div>
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-black">{t('lapPelajar')}</h1>
        <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString('ms-MY', { dateStyle: 'long' })}</p>
      </div>

      <div className="print:hidden mb-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-muted-foreground">{t('kelas')}
          <select value={kelasId} onChange={(e) => setKelasId(e.target.value)} className={`${inputCls} ml-2`}>
            <option value="semua">{t('semua')}</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.nama}</option>)}
          </select>
        </label>
        <Button onClick={load} className="h-10 bg-brand text-white hover:bg-brand-royal">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('tapis')}</Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={doCsv} className="h-10"><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          <Button variant="outline" onClick={() => printLaporan(t('lapPelajar'))} className="h-10"><Printer className="h-4 w-4 mr-1.5" />{t('cetak')}</Button>
        </div>
      </div>

      {loading ? <CardSkeleton count={3} /> : (
        <DataTable
          testid="lap-pelajar"
          rows={rows}
          searchKeys={[(r) => r.nama, (r) => r.kelas]}
          statusOptions={[
            { key: 'belum', label: t('stBelum'), filter: (r) => r.status === 'belum' },
            { key: 'separa', label: t('stSepara'), filter: (r) => r.status === 'separa' },
            { key: 'lunas', label: t('stLunas'), filter: (r) => r.status === 'lunas' },
          ]}
          columns={[
            { key: 'nama', header: t('pelajar'), sortable: true, sortValue: (r) => r.nama, render: (r) => <span className="block truncate font-medium text-foreground">{r.nama}</span> },
            { key: 'kelas', header: t('kelas'), sortable: true, sortValue: (r) => r.kelas, render: (r) => r.kelas },
            { key: 'bil', header: t('bil'), align: 'right', sortable: true, sortValue: (r) => r.bil, render: (r) => <span className="tabular">{r.bil}</span> },
            { key: 'jumlah', header: t('jumlahBil'), align: 'right', sortable: true, sortValue: (r) => r.jumlah,
              render: (r) => <span className="font-mono tabular">{formatRM(r.jumlah)}</span> },
            { key: 'bayar', header: t('dibayar'), align: 'right', sortable: true, sortValue: (r) => r.bayar,
              render: (r) => <span className="font-mono tabular text-emerald-600 dark:text-emerald-400">{formatRM(r.bayar)}</span> },
            { key: 'baki', header: t('baki'), align: 'right', sortable: true, sortValue: (r) => r.baki,
              render: (r) => <span className="font-mono font-bold tabular text-red-600 dark:text-red-400">{formatRM(r.baki)}</span> },
            { key: 'status', header: t('status'), render: (r) => <StatusPill status={r.status} /> },
          ]}
          tfoot={(
            <tr className="bg-muted font-bold">
              <td colSpan={3} className="px-5 py-3 text-right">{t('jumlah')} ({rows.length} {t('murid')})</td>
              <td className="px-5 py-3 font-mono tabular text-right">{formatRM(totals.jumlah)}</td>
              <td className="px-5 py-3 font-mono tabular text-right">{formatRM(totals.bayar)}</td>
              <td className="px-5 py-3 font-mono tabular text-right">{formatRM(totals.baki)}</td>
              <td />
            </tr>
          )}
          empty={{ icon: Users, title: t('tiadaData'), description: t('tiadaDataDesc') }}
        />
      )}
    </div>
  );
}
